import React from "react"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom"
import QuarterlyReportGenerator from "@/QuarterlyReportGenerator/index"
import QuarterlyReportIndex from "@/QuarterlyReport/Index"
import QuarterlyReportShow from "@/QuarterlyReport/Show"

import PdfView from "@/QuarterlyReport/PdfView"
import LpHighLevelKpis from "@/QuarterlyReport/LpHighLevelKpis"
import LpInvestmentPerformance from "@/QuarterlyReport/LpInvestmentPerformance"
import AttributionExposureLetter from "@/QuarterlyReport/AttributionExposureLetter"
import ExposureBySector from "@/QuarterlyReport/ExposureBySector"
import ExposureByGeography from "@/QuarterlyReport/ExposureByGeography"

import CoverLetter from "@/QuarterlyReport/CoverLetter"
import TableOfContents from "@/QuarterlyReport/TableOfContents"
import QpcspIntroLetter from "@/QuarterlyReport/QpcspIntroLetter"
import LpCapitalStatement from "@/QuarterlyReport/LpCapitalStatement"
import LogosCompanyCounts from "@/QuarterlyReport/LogosCompanyCounts"
import HistoricalInvestmentPerformance from "@/QuarterlyReport/HistoricalInvestmentPerformance"
import QpcspFinancialStatement from "@/QuarterlyReport/QpcspFinancialStatement"
import { CommunicationProvider } from "@/QuarterlyReport/CommunicationContext"
import { QuarterlyReportProvider } from "@/QuarterlyReport/useQuarterlyReportContext"
import ErrorBoundary from "@/ErrorBoundary"
import { ReportSection, Communication } from "~/api/communications"
import { QuarterlyReport } from "~/api/quarterly_reports"

import { path } from "static-path"

export const generatorPath = path("/manage/communications/:communicationId")
export const reportsPath = path(
  "/manage/communications/:communicationId/quarterly_reports",
)
export const reportShowPath = path(
  "/manage/communications/:communicationId/quarterly_reports/:reportId",
)

export const reportSectionCoverLetterPath = path(
  "/manage/communications/:communicationId/quarterly_reports/:reportId/cover_letter",
)

export const reportSectionTableOfContentsPath = path(
  "/manage/communications/:communicationId/quarterly_reports/:reportId/table_of_contents",
)

export const reportSectionLpHighLevelKpisPath = path(
  "/manage/communications/:communicationId/quarterly_reports/:reportId/lp_high_level_kpis",
)

export const reportSectionLpInvestmentPerformancePath = path(
  "/manage/communications/:communicationId/quarterly_reports/:reportId/lp_investment_performance",
)

export const reportSectionAttributionExposureLetterPath = path(
  "/manage/communications/:communicationId/quarterly_reports/:reportId/attribution_exposure_letter",
)

export const reportSectionExposureBySectorPath = path(
  "/manage/communications/:communicationId/quarterly_reports/:reportId/exposure_by_sector",
)

export const reportSectionExposureByGeographyPath = path(
  "/manage/communications/:communicationId/quarterly_reports/:reportId/exposure_by_geography",
)

export const reportSectionQpcspIntroLetterPath = path(
  "/manage/communications/:communicationId/quarterly_reports/:reportId/qpcsp_intro_letter",
)

export const reportSectionLpCapitalStatementPath = path(
  "/manage/communications/:communicationId/quarterly_reports/:reportId/lp_capital_statement",
)

export const reportSectionLogosCompanyCountsPath = path(
  "/manage/communications/:communicationId/quarterly_reports/:reportId/logos_company_counts",
)

export const reportSectionHistoricalInvestmentPerformancePath = path(
  "/manage/communications/:communicationId/quarterly_reports/:reportId/historical_investment_performance",
)

export const reportSectionQpcspFinancialStatementPath = path(
  "/manage/communications/:communicationId/quarterly_reports/:reportId/qpcsp_financial_statement",
)

const queryClient = new QueryClient()

interface QuarterlyReportAppProps {
  communicationId: string
  reportId?: string
  section?: ReportSection | string
  view?: "generator" | "quarterly_reports" | "pdf"
  reportData?: QuarterlyReport
  communicationData?: Communication
}

const QuarterlyReportApp = ({
  communicationId,
  reportId,
  section,
  view = "generator",
  reportData,
  communicationData,
}: QuarterlyReportAppProps) => {
  const initialPath = (() => {
    if (view === "pdf" && reportId) {
      // PDF mode - render PdfView component directly
      return null // Will be handled by direct component rendering
    } else if (reportId && section) {
      // If both reportId and section are provided, determine which section-specific route to use
      if (section === ReportSection.COVER_LETTER) {
        return reportSectionCoverLetterPath({ communicationId, reportId })
      }

      if (section === ReportSection.TABLE_OF_CONTENTS) {
        return reportSectionTableOfContentsPath({ communicationId, reportId })
      }

      if (section === ReportSection.LP_HIGH_LEVEL_KPIS) {
        return reportSectionLpHighLevelKpisPath({ communicationId, reportId })
      }

      if (section === ReportSection.LP_INVESTMENT_PERFORMANCE) {
        return reportSectionLpInvestmentPerformancePath({
          communicationId,
          reportId,
        })
      }

      if (section === ReportSection.ATTRIBUTION_EXPOSURE_LETTER) {
        return reportSectionAttributionExposureLetterPath({
          communicationId,
          reportId,
        })
      }

      if (section === ReportSection.EXPOSURE_BY_SECTOR) {
        return reportSectionExposureBySectorPath({ communicationId, reportId })
      }

      if (section === ReportSection.EXPOSURE_BY_GEOGRAPHY) {
        return reportSectionExposureByGeographyPath({
          communicationId,
          reportId,
        })
      }

      if (section === ReportSection.QPCSP_INTRO_LETTER) {
        return reportSectionQpcspIntroLetterPath({ communicationId, reportId })
      }

      if (section === ReportSection.LP_CAPITAL_STATEMENT) {
        return reportSectionLpCapitalStatementPath({
          communicationId,
          reportId,
        })
      }

      if (section === ReportSection.LOGOS_COMPANY_COUNTS) {
        return reportSectionLogosCompanyCountsPath({
          communicationId,
          reportId,
        })
      }

      if (section === ReportSection.HISTORICAL_INVESTMENT_PERFORMANCE) {
        return reportSectionHistoricalInvestmentPerformancePath({
          communicationId,
          reportId,
        })
      }

      if (section === ReportSection.QPCSP_FINANCIAL_STATEMENT) {
        return reportSectionQpcspFinancialStatementPath({
          communicationId,
          reportId,
        })
      }
      // Default to the report show page if section doesn't have a specific route
      return reportShowPath({ communicationId, reportId })
    } else if (reportId) {
      // If only reportId is provided, go to the report show page
      return reportShowPath({ communicationId, reportId })
    } else if (view === "quarterly_reports") {
      // If view is quarterly_reports, go to the reports list
      return reportsPath({ communicationId })
    } else {
      // Default to the generator
      return generatorPath({ communicationId })
    }
  })()

  // If in PDF mode, render PdfView directly without routing or API calls
  if (view === "pdf" && reportId) {
    return (
      <QueryClientProvider client={queryClient}>
        <ErrorBoundary>
          <PdfView
            reportData={reportData}
            communicationData={communicationData}
            sectionName={typeof section === "string" ? section : undefined}
          />
        </ErrorBoundary>
      </QueryClientProvider>
    )
  }

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ErrorBoundary>
          <CommunicationProvider communicationId={communicationId}>
            <Routes>
              <Route
                path={generatorPath.pattern}
                element={<QuarterlyReportGenerator />}
              />
              <Route
                path={reportsPath.pattern}
                element={<QuarterlyReportIndex />}
              />

              <Route
                path={reportShowPath.pattern}
                element={
                  <QuarterlyReportProvider>
                    <QuarterlyReportShow />
                  </QuarterlyReportProvider>
                }
              />
              <Route
                path={reportSectionCoverLetterPath.pattern}
                element={
                  <QuarterlyReportProvider>
                    <CoverLetter />
                  </QuarterlyReportProvider>
                }
              />
              <Route
                path={reportSectionTableOfContentsPath.pattern}
                element={
                  <QuarterlyReportProvider>
                    <TableOfContents />
                  </QuarterlyReportProvider>
                }
              />
              <Route
                path={reportSectionLpHighLevelKpisPath.pattern}
                element={
                  <QuarterlyReportProvider>
                    <LpHighLevelKpis />
                  </QuarterlyReportProvider>
                }
              />
              <Route
                path={reportSectionLpInvestmentPerformancePath.pattern}
                element={
                  <QuarterlyReportProvider>
                    <LpInvestmentPerformance />
                  </QuarterlyReportProvider>
                }
              />
              <Route
                path={reportSectionAttributionExposureLetterPath.pattern}
                element={
                  <QuarterlyReportProvider>
                    <AttributionExposureLetter />
                  </QuarterlyReportProvider>
                }
              />
              <Route
                path={reportSectionExposureBySectorPath.pattern}
                element={
                  <QuarterlyReportProvider>
                    <ExposureBySector />
                  </QuarterlyReportProvider>
                }
              />
              <Route
                path={reportSectionExposureByGeographyPath.pattern}
                element={
                  <QuarterlyReportProvider>
                    <ExposureByGeography />
                  </QuarterlyReportProvider>
                }
              />

              <Route
                path={reportSectionQpcspIntroLetterPath.pattern}
                element={
                  <QuarterlyReportProvider>
                    <QpcspIntroLetter />
                  </QuarterlyReportProvider>
                }
              />
              <Route
                path={reportSectionLpCapitalStatementPath.pattern}
                element={
                  <QuarterlyReportProvider>
                    <LpCapitalStatement />
                  </QuarterlyReportProvider>
                }
              />
              <Route
                path={reportSectionLogosCompanyCountsPath.pattern}
                element={
                  <QuarterlyReportProvider>
                    <LogosCompanyCounts />
                  </QuarterlyReportProvider>
                }
              />
              <Route
                path={reportSectionHistoricalInvestmentPerformancePath.pattern}
                element={
                  <QuarterlyReportProvider>
                    <HistoricalInvestmentPerformance />
                  </QuarterlyReportProvider>
                }
              />
              <Route
                path={reportSectionQpcspFinancialStatementPath.pattern}
                element={
                  <QuarterlyReportProvider>
                    <QpcspFinancialStatement />
                  </QuarterlyReportProvider>
                }
              />
              <Route path="*" element={<Navigate to={initialPath} replace />} />
            </Routes>
          </CommunicationProvider>
        </ErrorBoundary>
      </BrowserRouter>
    </QueryClientProvider>
  )
}

export default QuarterlyReportApp
